import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal, Select, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckCircleFilled, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { BatchInviteModal } from '../components/BatchInviteModal';
import { QuickOutreachModal } from '../components/QuickOutreachModal';
import { TargetInviteModal } from '../components/TargetInviteModal';
import { StatusMark } from '../../../components/StatusMark';
import { useTranslation } from '../../../i18n';
import { useCurrentAccount } from '../layout/currentAccountContext';
import { useSelectedShop } from '../layout/selectedShopContext';
import {
  cancelInfluencerBlacklist,
  cancelInfluencerCollection,
  convertInfluencerIntention,
  getInfluencerBlacklistPage,
  getInfluencerCollectionPage,
  getInfluencerCooperationPage,
  getInfluencerIntentionPage,
} from '../../../store/request';
import type {
  InfluencerBlacklistRecord,
  InfluencerCollectionRecord,
  InfluencerCooperationRecord,
  InfluencerIntentionRecord,
} from '../../../types/influencer';
import { AddBlacklistModal } from './AddBlacklistModal';
import type { CreatorManagerTab } from './creatorManagerData';

const regionLabelKey: Record<string, string> = {
  VN: 'talent.market_vn',
  TH: 'talent.market_th',
  ID: 'talent.market_id',
  MY: 'talent.market_my',
  PH: 'talent.market_ph',
  SG: 'talent.market_sg',
};

function formatCount(value?: number) {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toLocaleString('en-US');
}

function formatGmv(amount?: number, currency?: string) {
  if (amount == null || !Number.isFinite(amount)) return '—';
  const text = amount.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return currency ? `${currency} ${text}` : text;
}

function collectionRowKey(row: InfluencerCollectionRecord) {
  if (row.influencerId?.trim()) return row.influencerId.trim();
  if (row.id != null) return String(row.id);
  return row.creatorOpenId || row.username || '';
}

function intentionRowKey(row: InfluencerIntentionRecord) {
  if (row.influencerId?.trim()) return row.influencerId.trim();
  if (row.id != null) return String(row.id);
  return row.username || '';
}

function cooperationRowKey(row: InfluencerCooperationRecord) {
  if (row.id != null) return String(row.id);
  return [row.shopId, row.influencerId, row.targetCollaborationId, row.collaborationId]
    .filter((item) => item != null && String(item).trim())
    .join('-');
}

type OutreachRow = {
  influencerId?: string;
  creatorOpenId?: string;
  nickname?: string;
  username?: string;
  email?: string;
  avatarUrl?: string;
  selectionRegion?: string;
  categoryNames?: string[];
};

function formatTime(value?: string) {
  if (!value) return '—';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  return parsed.format('YYYY-MM-DD HH:mm:ss');
}

function blacklistRowKey(row: InfluencerBlacklistRecord) {
  if (row.influencerId?.trim()) return row.influencerId.trim();
  if (row.id != null) return String(row.id);
  return row.creatorOpenId || row.username || '';
}

export const CreatorManager = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const account = useCurrentAccount();
  const { shops, shopId } = useSelectedShop();
  const [tab, setTab] = useState<CreatorManagerTab>('mine');
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [records, setRecords] = useState<InfluencerCollectionRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [listVersion, setListVersion] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [outreachOpen, setOutreachOpen] = useState(false);
  const [outreachPresetIds, setOutreachPresetIds] = useState<string[]>([]);
  const [outreachPresets, setOutreachPresets] = useState<InfluencerCollectionRecord[]>([]);
  const [outreachChannel, setOutreachChannel] = useState<'platform_email' | 'private_message'>('platform_email');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [batchInviteOpen, setBatchInviteOpen] = useState(false);
  const [inviteTalentIds, setInviteTalentIds] = useState<string[]>([]);
  const [blacklistOpen, setBlacklistOpen] = useState(false);
  const [blacklistTarget, setBlacklistTarget] = useState<{ influencerId: string; talentName: string } | null>(
    null
  );
  const [blacklistRecords, setBlacklistRecords] = useState<InfluencerBlacklistRecord[]>([]);
  const [blacklistTotal, setBlacklistTotal] = useState(0);
  const [blacklistLoading, setBlacklistLoading] = useState(false);
  const [blacklistHistory, setBlacklistHistory] = useState(false);
  const [blacklistVersion, setBlacklistVersion] = useState(0);
  const [intentionRecords, setIntentionRecords] = useState<InfluencerIntentionRecord[]>([]);
  const [intentionTotal, setIntentionTotal] = useState(0);
  const [intentionLoading, setIntentionLoading] = useState(false);
  const [lastChannel, setLastChannel] = useState<1 | 2 | undefined>(undefined);
  const [cooperationRecords, setCooperationRecords] = useState<InfluencerCooperationRecord[]>([]);
  const [cooperationTotal, setCooperationTotal] = useState(0);
  const [cooperationLoading, setCooperationLoading] = useState(false);

  const tabItems = useMemo(
    () => [
      { key: 'mine' as const, label: t('creator.tab_mine') },
      { key: 'blacklist' as const, label: t('creator.tab_blacklist') },
      { key: 'collab' as const, label: t('creator.tab_collab') },
      { key: 'intent' as const, label: t('creator.tab_intent') },
    ],
    [t]
  );

  const isCollectionTab = tab === 'mine';
  const isCollabTab = tab === 'collab';
  const isIntentTab = tab === 'intent';

  const openOutreach = (
    rows: OutreachRow[],
    channel: 'platform_email' | 'private_message' = 'platform_email'
  ) => {
    const ids = rows
      .map((row) => row.influencerId?.trim() || row.creatorOpenId?.trim())
      .filter((id): id is string => Boolean(id));
    setOutreachPresets(rows);
    setOutreachPresetIds(ids);
    setOutreachChannel(channel);
    setOutreachOpen(true);
  };

  const openInvite = (ids: string[]) => {
    setInviteTalentIds(ids);
    setInviteOpen(true);
  };

  const selectedRecords = useMemo(() => {
    const keys = new Set(selectedRowKeys.map(String));
    return records.filter((row) => keys.has(collectionRowKey(row)));
  }, [records, selectedRowKeys]);

  const selectedIntentionRecords = useMemo(() => {
    const keys = new Set(selectedRowKeys.map(String));
    return intentionRecords.filter((row) => keys.has(intentionRowKey(row)));
  }, [intentionRecords, selectedRowKeys]);

  const selectedCooperationRecords = useMemo(() => {
    const keys = new Set(selectedRowKeys.map(String));
    return cooperationRecords.filter((row) => keys.has(cooperationRowKey(row)));
  }, [cooperationRecords, selectedRowKeys]);

  const selectedOutreachRows = isIntentTab
    ? selectedIntentionRecords
    : isCollabTab
      ? selectedCooperationRecords
      : selectedRecords;
  const selectedInfluencerIds = useMemo(
    () =>
      selectedOutreachRows
        .map((row) => row.influencerId?.trim())
        .filter((id): id is string => Boolean(id)),
    [selectedOutreachRows]
  );

  const hasSelection = selectedOutreachRows.length > 0;

  useEffect(() => {
    if (!isCollectionTab) return;
    const userCode = account?.userCode?.trim();
    if (!userCode) {
      setRecords([]);
      setTotal(0);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getInfluencerCollectionPage({
      userCode,
      pageNum,
      pageSize,
      ...(appliedKeyword ? { keyword: appliedKeyword } : {}),
    })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          message.error(res.message || t('creator.load_failed'));
          setRecords([]);
          setTotal(0);
          return;
        }
        const page = res.content;
        const list = page?.records;
        setRecords(Array.isArray(list) ? list : []);
        setTotal(typeof page?.total === 'number' ? page.total : 0);
      })
      .catch(() => {
        if (!cancelled) message.error(t('creator.load_failed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isCollectionTab, account?.userCode, pageNum, pageSize, appliedKeyword, listVersion, t]);

  useEffect(() => {
    if (!isCollabTab) return;
    const userCode = account?.userCode?.trim();
    if (!userCode) {
      setCooperationRecords([]);
      setCooperationTotal(0);
      return;
    }
    let cancelled = false;
    setCooperationLoading(true);
    getInfluencerCooperationPage({
      userCode,
      pageNum,
      pageSize,
      ...(shopId ? { shopId } : {}),
      ...(appliedKeyword ? { keyword: appliedKeyword } : {}),
    })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          message.error(res.message || t('creator.collab_load_failed'));
          setCooperationRecords([]);
          setCooperationTotal(0);
          return;
        }
        const page = res.content;
        const list = page?.records;
        setCooperationRecords(Array.isArray(list) ? list : []);
        setCooperationTotal(typeof page?.total === 'number' ? page.total : 0);
      })
      .catch(() => {
        if (!cancelled) message.error(t('creator.collab_load_failed'));
      })
      .finally(() => {
        if (!cancelled) setCooperationLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isCollabTab, account?.userCode, shopId, pageNum, pageSize, appliedKeyword, listVersion, t]);

  useEffect(() => {
    if (!isIntentTab) return;
    const userCode = account?.userCode?.trim();
    if (!userCode) {
      setIntentionRecords([]);
      setIntentionTotal(0);
      return;
    }
    let cancelled = false;
    setIntentionLoading(true);
    getInfluencerIntentionPage({
      userCode,
      pageNum,
      pageSize,
      ...(appliedKeyword ? { keyword: appliedKeyword } : {}),
      ...(lastChannel != null ? { lastChannel } : {}),
    })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          message.error(res.message || t('creator.intent_load_failed'));
          setIntentionRecords([]);
          setIntentionTotal(0);
          return;
        }
        const page = res.content;
        const list = page?.records;
        setIntentionRecords(Array.isArray(list) ? list : []);
        setIntentionTotal(typeof page?.total === 'number' ? page.total : 0);
      })
      .catch(() => {
        if (!cancelled) message.error(t('creator.intent_load_failed'));
      })
      .finally(() => {
        if (!cancelled) setIntentionLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isIntentTab, account?.userCode, pageNum, pageSize, appliedKeyword, lastChannel, listVersion, t]);

  useEffect(() => {
    if (tab !== 'blacklist') return;
    const userCode = account?.userCode?.trim();
    if (!userCode) {
      setBlacklistRecords([]);
      setBlacklistTotal(0);
      return;
    }
    let cancelled = false;
    setBlacklistLoading(true);
    getInfluencerBlacklistPage({
      userCode,
      pageNum,
      pageSize,
      ...(blacklistHistory ? { deleted: 1 as const } : {}),
    })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          message.error(res.message || t('creator.blacklist_load_failed'));
          setBlacklistRecords([]);
          setBlacklistTotal(0);
          return;
        }
        const page = res.content;
        const list = page?.records;
        setBlacklistRecords(Array.isArray(list) ? list : []);
        setBlacklistTotal(typeof page?.total === 'number' ? page.total : 0);
      })
      .catch(() => {
        if (!cancelled) message.error(t('creator.blacklist_load_failed'));
      })
      .finally(() => {
        if (!cancelled) setBlacklistLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, account?.userCode, pageNum, pageSize, blacklistHistory, blacklistVersion, t]);

  const confirmUnfavorite = useCallback((row: InfluencerCollectionRecord) => {
    const influencerId = row.influencerId?.trim();
    const name = row.nickname || row.username || '—';
    if (!influencerId) {
      message.warning(t('talent.missing_open_id'));
      return;
    }
    Modal.confirm({
      title: t('creator.unfavorite'),
      content: t('creator.unfavorite_confirm', { name }),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      centered: true,
      onOk: async () => {
        const res = await cancelInfluencerCollection(influencerId);
        if (res.code !== 200) {
          message.error(res.message || t('creator.unfavorite_failed'));
          return Promise.reject(new Error(res.message || 'cancel failed'));
        }
        message.success(res.message || t('creator.unfavorite_success', { name }));
        setSelectedRowKeys((prev) => prev.filter((key) => String(key) !== collectionRowKey(row)));
        setListVersion((value) => value + 1);
      },
    });
  }, [t]);

  const confirmRemoveBlacklist = useCallback(
    (row: InfluencerBlacklistRecord) => {
      const influencerId = row.influencerId?.trim();
      const name = row.nickname || row.username || '—';
      if (!influencerId) {
        message.warning(t('creator.blacklist_missing_remove_id'));
        return;
      }
      Modal.confirm({
        title: t('creator.remove'),
        content: t('creator.remove_confirm', { name }),
        okText: t('common.confirm'),
        cancelText: t('common.cancel'),
        centered: true,
        onOk: async () => {
          const res = await cancelInfluencerBlacklist(influencerId);
          if (res.code !== 200) {
            message.error(res.message || t('creator.blacklist_remove_failed'));
            return Promise.reject(new Error(res.message || 'remove failed'));
          }
          message.success(res.message || t('creator.blacklist_removed', { name }));
          setBlacklistVersion((value) => value + 1);
        },
      });
    },
    [t]
  );

  const confirmConvert = useCallback(
    (row: InfluencerIntentionRecord) => {
      const influencerId = row.influencerId?.trim();
      const name = row.nickname || row.username || '—';
      const currentShopId = shopId?.trim();
      if (!influencerId) {
        message.warning(t('creator.blacklist_missing_id'));
        return;
      }
      if (!currentShopId) {
        message.warning(t('invitation.invite_shop_required'));
        return;
      }
      const userId = account?.userCode?.trim();
      Modal.confirm({
        title: t('creator.convert_collab'),
        content: t('creator.convert_confirm', { name }),
        okText: t('common.confirm'),
        cancelText: t('common.cancel'),
        centered: true,
        onOk: async () => {
          const res = await convertInfluencerIntention(influencerId, {
            shopId: currentShopId,
            ...(userId ? { userId } : {}),
          });
          if (res.code !== 200 || res.content === false) {
            if (res.code !== -1) message.error(res.message || t('creator.convert_failed'));
            return Promise.reject(new Error(res.message || 'convert failed'));
          }
          message.success(res.message || t('creator.converted_collab', { name }));
          setListVersion((value) => value + 1);
        },
      });
    },
    [account?.userCode, shopId, t]
  );

  const applyKeyword = (value: string) => {
    setPageNum(1);
    setAppliedKeyword(value.trim());
    setSelectedRowKeys([]);
  };

  const onTabChange = (key: string) => {
    setTab(key as CreatorManagerTab);
    setSelectedRowKeys([]);
    setKeyword('');
    setAppliedKeyword('');
    setLastChannel(undefined);
    setPageNum(1);
  };

  const mineColumns = useMemo<ColumnsType<InfluencerCollectionRecord>>(
    () => [
      {
        title: t('creator.col_talent'),
        key: 'talent',
        render: (_, row) => {
          const name = row.nickname || row.username || '—';
          const region = row.selectionRegion;
          const regionKey = region ? regionLabelKey[region] : undefined;
          const regionText = region ? (regionKey ? `${t(regionKey)} ${region}` : region) : '';
          const categories = row.categoryNames?.filter(Boolean) ?? [];
          return (
            <div className="flex items-start gap-3 py-1">
              {row.avatarUrl ? (
                <img
                  src={row.avatarUrl}
                  alt=""
                  className="size-10 shrink-0 rounded-full object-cover"
                />
              ) : (
                <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-[#596ae8] text-sm font-semibold text-white">
                  {(name === '—' ? '?' : name).slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="min-w-0">
                <div className="font-semibold text-slate-800">
                  {name}{' '}
                  {row.username ? (
                    <span className="text-xs font-normal text-slate-400">@{row.username}</span>
                  ) : null}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {regionText ? (
                    <Tag className="m-0 border-0 bg-slate-100 text-xs text-slate-600">{regionText}</Tag>
                  ) : null}
                  {categories.map((item) => (
                    <Tag key={item} className="m-0 border-0 bg-slate-100 text-xs text-slate-600">
                      {item}
                    </Tag>
                  ))}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        title: t('creator.col_tags'),
        dataIndex: 'label',
        render: (label?: number) => {
          if (label === 1) {
            return <StatusMark tone="success">{t('creator.tag_coop')}</StatusMark>;
          }
          if (label === 2) {
            return <StatusMark tone="pending">{t('creator.tag_intent')}</StatusMark>;
          }
          if (label === 0) {
            return <StatusMark tone="muted">{t('creator.tag_none')}</StatusMark>;
          }
          return '—';
        },
      },
      {
        title: t('creator.gmv30'),
        dataIndex: 'gmvAmount',
        render: (_: number | undefined, row) => formatGmv(row.gmvAmount, row.gmvCurrency),
      },
      {
        title: t('creator.followers'),
        dataIndex: 'followerCount',
        render: (value?: number) => formatCount(value),
      },
      {
        title: t('creator.col_actions'),
        key: 'actions',
        render: (_, row) => (
          <div className="flex flex-wrap items-center gap-0.5">
            {row.influencerId?.trim() ? (
              <Button
                type="link"
                className="wb-text-link"
                href={`/creator/talent_square/${encodeURIComponent(row.influencerId.trim())}`}
                target="_blank"
              >
                {t('common.detail')}
              </Button>
            ) : null}
            <Button type="link" className="wb-text-link" onClick={() => openOutreach([row])}>
              {t('creator.outreach')}
            </Button>
            <Button
              type="link"
              className="wb-text-link wb-text-link--active"
              onClick={() => confirmUnfavorite(row)}
            >
              {t('creator.unfavorite')}
            </Button>
            <Button
              type="link"
              danger
              className="wb-text-link"
              onClick={() => {
                const influencerId = row.influencerId?.trim();
                if (!influencerId) {
                  message.warning(t('creator.blacklist_missing_id'));
                  return;
                }
                setBlacklistTarget({
                  influencerId,
                  talentName: row.nickname || row.username || '—',
                });
                setBlacklistOpen(true);
              }}
            >
              {t('creator.add_blacklist')}
            </Button>
          </div>
        ),
      },
    ],
    [t, confirmUnfavorite]
  );

  const collabColumns = useMemo<ColumnsType<InfluencerCooperationRecord>>(() => {
    const shopNameOf = (id?: string) => {
      const text = id?.trim();
      if (!text) return '—';
      return shops.find((shop) => shop.shopId === text)?.shopName?.trim() || text;
    };
    return [
      {
        title: t('creator.col_talent'),
        key: 'talent',
        width: 280,
        className: 'creator-talent-cell',
        render: (_, row) => {
          const name = row.nickname || row.username || '—';
          const region = row.selectionRegion;
          const regionKey = region ? regionLabelKey[region] : undefined;
          const regionText = region ? (regionKey ? `${t(regionKey)} ${region}` : region) : '';
          const categories = row.categoryNames?.filter(Boolean) ?? [];
          return (
            <div className="flex min-w-0 items-start gap-3 py-1">
              {row.avatarUrl ? (
                <img src={row.avatarUrl} alt="" className="size-10 shrink-0 rounded-full object-cover" />
              ) : (
                <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-[#596ae8] text-sm font-semibold text-white">
                  {(name === '—' ? '?' : name).slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="break-words font-semibold text-slate-800">
                  {name}{' '}
                  {row.username ? (
                    <span className="text-xs font-normal text-slate-400">@{row.username}</span>
                  ) : null}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {regionText ? (
                    <Tag className="m-0 border-0 bg-slate-100 text-xs text-slate-600">{regionText}</Tag>
                  ) : null}
                  {categories.map((item) => (
                    <Tag key={item} className="m-0 border-0 bg-slate-100 text-xs text-slate-600">
                      {item}
                    </Tag>
                  ))}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        title: t('creator.col_source'),
        dataIndex: 'source',
        width: 140,
        render: (value?: number) =>
          value === 1
            ? t('creator.source_invite_accepted')
            : value === 2
              ? t('creator.source_chat')
              : '—',
      },
      {
        title: t('creator.col_shop'),
        dataIndex: 'shopId',
        width: 160,
        ellipsis: true,
        render: (value?: string) => shopNameOf(value),
      },
      {
        title: t('creator.col_added_products'),
        dataIndex: 'addedProductCount',
        width: 120,
        render: (value?: number) => formatCount(value),
      },
      {
        title: t('creator.video_live'),
        key: 'videoLive',
        width: 120,
        render: (_, row) => `${formatCount(row.videoCount)} / ${formatCount(row.liveCount)}`,
      },
      {
        title: t('creator.gmv30'),
        dataIndex: 'gmvAmount',
        render: (_: number | undefined, row) => formatGmv(row.gmvAmount, row.gmvCurrency),
      },
      {
        title: t('creator.followers'),
        dataIndex: 'followerCount',
        render: (value?: number) => formatCount(value),
      },
      {
        title: t('creator.col_start'),
        dataIndex: 'acceptedAt',
        width: 180,
        render: (value?: string) => formatTime(value),
      },
      {
        title: t('creator.col_actions'),
        key: 'actions',
        render: (_, row) => (
          <div className="flex flex-wrap items-center gap-0.5">
            {row.influencerId?.trim() ? (
              <Button
                type="link"
                className="wb-text-link"
                href={`/creator/talent_square/${encodeURIComponent(row.influencerId.trim())}`}
                target="_blank"
              >
                {t('common.detail')}
              </Button>
            ) : null}
            <Button type="link" className="wb-text-link" onClick={() => openOutreach([row])}>
              {t('creator.outreach')}
            </Button>
            <Button
              type="link"
              danger
              className="wb-text-link"
              onClick={() => {
                const influencerId = row.influencerId?.trim();
                if (!influencerId) {
                  message.warning(t('creator.blacklist_missing_id'));
                  return;
                }
                setBlacklistTarget({
                  influencerId,
                  talentName: row.nickname || row.username || '—',
                });
                setBlacklistOpen(true);
              }}
            >
              {t('creator.add_blacklist')}
            </Button>
          </div>
        ),
      },
    ];
  }, [shops, t]);

  const intentColumns = useMemo<ColumnsType<InfluencerIntentionRecord>>(
    () => [
      {
        title: t('creator.col_talent'),
        key: 'talent',
        render: (_, row) => {
          const name = row.nickname || row.username || '—';
          const region = row.selectionRegion;
          const regionKey = region ? regionLabelKey[region] : undefined;
          const regionText = region ? (regionKey ? `${t(regionKey)} ${region}` : region) : '';
          const categories = row.categoryNames?.filter(Boolean) ?? [];
          return (
            <div className="flex items-start gap-3 py-1">
              <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-[#596ae8] text-sm font-semibold text-white">
                {(name === '—' ? '?' : name).slice(0, 1).toUpperCase()}
              </span>
              <div className="min-w-0">
                <div className="font-semibold text-slate-800">
                  {name}{' '}
                  {row.username ? (
                    <span className="text-xs font-normal text-slate-400">@{row.username}</span>
                  ) : null}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {regionText ? (
                    <Tag className="m-0 border-0 bg-slate-100 text-xs text-slate-600">{regionText}</Tag>
                  ) : null}
                  {categories.map((item) => (
                    <Tag key={item} className="m-0 border-0 bg-slate-100 text-xs text-slate-600">
                      {item}
                    </Tag>
                  ))}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        title: t('creator.col_channels'),
        dataIndex: 'lastChannel',
        width: 120,
        render: (value?: number) =>
          value === 1 ? t('creator.channel_dm') : value === 2 ? t('creator.channel_email') : '—',
      },
      {
        title: t('creator.col_reply'),
        key: 'reply',
        width: 180,
        render: (_, row) =>
          t('creator.reply_counts', {
            dm: formatCount(row.replyDmCount ?? 0),
            email: formatCount(row.replyEmailCount ?? 0),
          }),
      },
      {
        title: t('creator.col_last_interaction'),
        dataIndex: 'lastReplyAt',
        width: 180,
        render: (value?: string) => formatTime(value),
      },
      {
        title: t('creator.col_last_content'),
        dataIndex: 'lastReplyContent',
        ellipsis: true,
        render: (value?: string) => {
          const text = value?.trim();
          if (!text) return '—';
          return (
            <span className="inline-block max-w-[280px] truncate" title={text}>
              {text}
            </span>
          );
        },
      },
      {
        title: t('creator.gmv30'),
        dataIndex: 'gmvAmount',
        render: (_: number | undefined, row) => formatGmv(row.gmvAmount, row.gmvCurrency),
      },
      {
        title: t('creator.followers'),
        dataIndex: 'followerCount',
        render: (value?: number) => formatCount(value),
      },
      {
        title: t('creator.col_actions'),
        key: 'actions',
        render: (_, row) => (
          <div className="flex flex-wrap items-center gap-0.5">
            {row.influencerId?.trim() ? (
              <Button
                type="link"
                className="wb-text-link"
                href={`/creator/talent_square/${encodeURIComponent(row.influencerId.trim())}`}
                target="_blank"
              >
                {t('common.detail')}
              </Button>
            ) : null}
            <Button type="link" className="wb-text-link" onClick={() => openOutreach([row])}>
              {t('creator.outreach')}
            </Button>
            <Button type="link" className="wb-text-link" onClick={() => confirmConvert(row)}>
              {t('creator.convert_collab')}
            </Button>
            <Button
              type="link"
              danger
              className="wb-text-link"
              onClick={() => {
                const influencerId = row.influencerId?.trim();
                if (!influencerId) {
                  message.warning(t('creator.blacklist_missing_id'));
                  return;
                }
                setBlacklistTarget({
                  influencerId,
                  talentName: row.nickname || row.username || '—',
                });
                setBlacklistOpen(true);
              }}
            >
              {t('creator.add_blacklist')}
            </Button>
          </div>
        ),
      },
    ],
    [confirmConvert, t]
  );

  const blacklistColumns = useMemo<ColumnsType<InfluencerBlacklistRecord>>(
    () => [
      {
        title: t('creator.col_talent'),
        key: 'talent',
        render: (_, row) => {
          const name = row.nickname || row.username || '—';
          const region = row.selectionRegion;
          const regionKey = region ? regionLabelKey[region] : undefined;
          const regionText = region ? (regionKey ? `${t(regionKey)} ${region}` : region) : '';
          const categories = row.categoryNames?.filter(Boolean) ?? [];
          return (
            <div className="flex items-start gap-3 py-1">
              {row.avatarUrl ? (
                <img
                  src={row.avatarUrl}
                  alt=""
                  className="size-10 shrink-0 rounded-xl object-cover"
                />
              ) : (
                <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-[#596ae8] text-sm font-semibold text-white">
                  {(name === '—' ? '?' : name).slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-semibold text-slate-800">{name}</span>
                  {row.username ? <CheckCircleFilled className="text-[14px] text-[#22c55e]" /> : null}
                  {row.username ? (
                    <span className="text-xs font-normal text-slate-400">@{row.username}</span>
                  ) : null}
                  {row.deleted ? <StatusMark tone="muted">{t('creator.removed_tag')}</StatusMark> : null}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {regionText ? (
                    <Tag className="m-0 border-0 bg-slate-100 text-xs text-slate-600">{regionText}</Tag>
                  ) : null}
                  {categories.map((item) => (
                    <Tag key={item} className="m-0 border-0 bg-slate-100 text-xs text-slate-600">
                      {item}
                    </Tag>
                  ))}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        title: t('creator.col_reason'),
        key: 'reason',
        width: 240,
        ellipsis: true,
        render: (_, row) => {
          const label =
            row.reasonType === 1
              ? t('creator.reason_timeout')
              : row.reasonType === 2
                ? t('creator.reason_no_update')
                : row.reasonType === 3
                  ? t('creator.reason_fraud')
                  : row.reasonType === 99
                    ? t('creator.reason_other')
                    : row.reasonType != null
                      ? String(row.reasonType)
                      : '';
          const text = [label, row.remark].filter(Boolean).join('：');
          if (!text) return '—';
          return (
            <span className="inline-flex max-w-[280px] items-center gap-1.5 text-[#e11d48]" title={text}>
              <span className="inline-block size-1.5 shrink-0 rounded-full bg-[#e11d48]" />
              <span className="truncate">{text}</span>
            </span>
          );
        },
      },
      {
        title: t('creator.col_added_by'),
        key: 'addedBy',
        render: (_, row) => row.createdName?.trim() || row.createdBy?.trim() || '—',
      },
      {
        title: t('creator.col_added_at'),
        dataIndex: 'createdAt',
        render: (value?: string) => formatTime(value),
      },
      {
        title: t('creator.col_actions'),
        key: 'actions',
        render: (_, row) =>
          blacklistHistory || row.deleted ? (
            '—'
          ) : (
            <Button type="link" className="wb-text-link" onClick={() => confirmRemoveBlacklist(row)}>
              {t('creator.remove')}
            </Button>
          ),
      },
    ],
    [blacklistHistory, confirmRemoveBlacklist, t]
  );

  return (
    <div className="container">
      <header className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="mb-0 mt-0 text-[22px] font-semibold leading-snug tracking-tight text-slate-800">
          {t('creator.title')}
        </h1>
      </header>
      <div className=" pb-4 pt-4 ">
        <div className="wb-pill-tabs" role="tablist" aria-label={t('creator.tabs_label')}>
          {tabItems.map((item) => {
            const active = tab === item.key;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={`wb-pill-tabs-item${active ? ' wb-pill-tabs-item--active' : ''}`}
                onClick={() => onTabChange(item.key)}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>
      <section className="wb-section !p-0 overflow-hidden">


        {isCollectionTab || isCollabTab || isIntentTab ? (
          <>
            <div className="flex flex-wrap items-center gap-2 border-t border-[#eef0f4] px-4 py-3 md:px-5">
              <Input
                allowClear
                className="max-w-[220px] rounded-lg"
                placeholder={t('creator.search_placeholder')}
                prefix={<SearchOutlined className="text-slate-400" />}
                value={keyword}
                onChange={(e) => {
                  const next = e.target.value;
                  setKeyword(next);
                  if (!next.trim()) applyKeyword('');
                }}
                onPressEnter={() => applyKeyword(keyword)}
              />
              {isIntentTab ? (
                <Select<1 | 2>
                  allowClear
                  className="w-[160px]"
                  placeholder={t('creator.last_channel')}
                  value={lastChannel}
                  options={[
                    { value: 1, label: t('creator.channel_dm') },
                    { value: 2, label: t('creator.channel_email') },
                  ]}
                  onChange={(value) => {
                    setLastChannel(value);
                    setPageNum(1);
                    setSelectedRowKeys([]);
                  }}
                />
              ) : null}
              <Button
                type="primary"
                className="wb-btn-primary rounded-lg"
                disabled={!hasSelection}
                onClick={() => openOutreach(selectedOutreachRows)}
              >
                {t('creator.batch_outreach')}
              </Button>
              <Button
                className="rounded-lg"
                disabled={!hasSelection}
                onClick={() => {
                  if (!shopId) {
                    message.warning(t('invitation.invite_shop_required'));
                    return;
                  }
                  setBatchInviteOpen(true);
                }}
              >
                {t('creator.batch_invite')}
              </Button>
              <Button className="rounded-lg" onClick={() => navigate('/creator/talent_square')}>
                {t('creator.find_talent')}
              </Button>

              <Button
                className="rounded-lg"
                disabled={!hasSelection}
                onClick={() => openInvite(selectedInfluencerIds)}
              >
                {t('creator.add_invite')}
              </Button>
              <Button
                className="rounded-lg"
                disabled={!hasSelection}
                onClick={() => openOutreach(selectedOutreachRows, 'private_message')}
              >
                {t('creator.send_dm')}
              </Button>
              <Button
                className="rounded-lg"
                disabled={!hasSelection}
                onClick={() => openOutreach(selectedOutreachRows, 'platform_email')}
              >
                {t('creator.send_email')}
              </Button>
              <span className="ml-auto text-xs text-slate-400">
                {t('creator.mine_count', {
                  count: isIntentTab ? intentionTotal : isCollabTab ? cooperationTotal : total,
                })}
              </span>
            </div>
            <div className="px-4 pb-4 md:px-5">
              {isCollabTab ? (
              <Table<InfluencerCooperationRecord>
                rowKey={(row) => cooperationRowKey(row)}
                dataSource={cooperationRecords}
                loading={cooperationLoading}
                scroll={{ x: 1280 }}
                pagination={{
                  current: pageNum,
                  pageSize,
                  total: cooperationTotal,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  onChange: (current, size) => {
                    setSelectedRowKeys([]);
                    if (size !== pageSize) {
                      setPageNum(1);
                      setPageSize(size);
                      return;
                    }
                    setPageNum(current);
                  },
                }}
                rowSelection={{
                  selectedRowKeys,
                  onChange: setSelectedRowKeys,
                }}
                columns={collabColumns}
              />
              ) : isIntentTab ? (
              <Table<InfluencerIntentionRecord>
                rowKey={(row) => intentionRowKey(row)}
                dataSource={intentionRecords}
                loading={intentionLoading}
                pagination={{
                  current: pageNum,
                  pageSize,
                  total: intentionTotal,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  onChange: (current, size) => {
                    setSelectedRowKeys([]);
                    if (size !== pageSize) {
                      setPageNum(1);
                      setPageSize(size);
                      return;
                    }
                    setPageNum(current);
                  },
                }}
                rowSelection={{
                  selectedRowKeys,
                  onChange: setSelectedRowKeys,
                }}
                columns={intentColumns}
              />
              ) : (
              <Table<InfluencerCollectionRecord>
                rowKey={(row) => collectionRowKey(row)}
                dataSource={records}
                loading={loading}
                pagination={{
                  current: pageNum,
                  pageSize,
                  total,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  onChange: (current, size) => {
                    setSelectedRowKeys([]);
                    if (size !== pageSize) {
                      setPageNum(1);
                      setPageSize(size);
                      return;
                    }
                    setPageNum(current);
                  },
                }}
                rowSelection={{
                  selectedRowKeys,
                  onChange: setSelectedRowKeys,
                }}
                columns={mineColumns}
              />
              )}
            </div>
          </>
        ) : null}

        {tab === 'blacklist' ? (
          <>
            <div
              className="wb-count-tabs border-b border-[#eef0f4] px-4 md:px-5"
              role="tablist"
              aria-label={t('creator.tab_blacklist')}
            >
              <button
                type="button"
                role="tab"
                aria-selected={!blacklistHistory}
                className={`wb-count-tabs-item${!blacklistHistory ? ' wb-count-tabs-item--active' : ''}`}
                onClick={() => {
                  setBlacklistHistory(false);
                  setPageNum(1);
                }}
              >
                {t('creator.blacklist_current')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={blacklistHistory}
                className={`wb-count-tabs-item${blacklistHistory ? ' wb-count-tabs-item--active' : ''}`}
                onClick={() => {
                  setBlacklistHistory(true);
                  setPageNum(1);
                }}
              >
                {t('creator.blacklist_history')}
              </button>
            </div>
            <div className="px-4 py-3 md:px-5">
              <Button
                type="primary"
                className="wb-btn-primary rounded-lg"
                icon={<PlusOutlined />}
                onClick={() => {
                  setBlacklistTarget(null);
                  setBlacklistOpen(true);
                }}
              >
                {t('creator.add_blacklist')}
              </Button>
            </div>
            <div className="px-4 pb-4 md:px-5">
              <Table<InfluencerBlacklistRecord>
                rowKey={(row) => blacklistRowKey(row)}
                dataSource={blacklistRecords}
                loading={blacklistLoading}
                pagination={{
                  current: pageNum,
                  pageSize,
                  total: blacklistTotal,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  onChange: (current, size) => {
                    if (size !== pageSize) {
                      setPageNum(1);
                      setPageSize(size);
                      return;
                    }
                    setPageNum(current);
                  },
                }}
                columns={blacklistColumns}
              />
            </div>
          </>
        ) : null}

      </section>

      <QuickOutreachModal
        open={outreachOpen}
        presetTalentIds={outreachPresetIds}
        presetTalents={outreachPresets}
        presetChannel={outreachChannel}
        onClose={() => {
          setOutreachOpen(false);
          setOutreachPresets([]);
          setOutreachPresetIds([]);
        }}
      />
      <BatchInviteModal
        open={batchInviteOpen}
        shopId={shopId}
        influencerIds={selectedInfluencerIds}
        onClose={() => setBatchInviteOpen(false)}
        onSuccess={() => {
          setSelectedRowKeys([]);
          setListVersion((value) => value + 1);
        }}
      />
      <TargetInviteModal
        open={inviteOpen}
        talentIds={inviteTalentIds}
        onClose={() => setInviteOpen(false)}
      />
      <AddBlacklistModal
        open={blacklistOpen}
        influencerId={blacklistTarget?.influencerId}
        talentName={blacklistTarget?.talentName}
        onClose={() => {
          setBlacklistOpen(false);
          setBlacklistTarget(null);
        }}
        onAdded={(influencerId) => {
          setRecords((prev) => {
            const next = prev.filter((row) => row.influencerId?.trim() !== influencerId);
            if (next.length !== prev.length) setTotal((value) => Math.max(0, value - 1));
            return next;
          });
          setIntentionRecords((prev) => {
            const next = prev.filter((row) => row.influencerId?.trim() !== influencerId);
            if (next.length !== prev.length) setIntentionTotal((value) => Math.max(0, value - 1));
            return next;
          });
          setCooperationRecords((prev) => {
            const removed = prev.filter((row) => row.influencerId?.trim() === influencerId).length;
            if (removed > 0) setCooperationTotal((value) => Math.max(0, value - removed));
            return prev.filter((row) => row.influencerId?.trim() !== influencerId);
          });
          setSelectedRowKeys((keys) => keys.filter((key) => String(key) !== influencerId));
          setBlacklistVersion((value) => value + 1);
          setListVersion((value) => value + 1);
        }}
      />
    </div>
  );
});
